import { Injectable, isDevMode } from '@angular/core';
import { RegisterTable, InverterRegister, RegisterUiMetadata } from 'ngx-modbus';
import {
  BuiltSectionSelector,
  ConfigFormSchema,
  FieldDefinition,
  FieldType,
  LeanFieldRef,
  LeanFormSchema,
  LeanModeSection,
  ModeSection,
  SelectOption,
} from '../types/config-form.types';

export interface BuilderDiagnostic {
  level: 'error' | 'warning';
  message: string;
  registerKey?: string;
  sectionId?: string;
}

@Injectable({ providedIn: 'root' })
export class ConfigFormBuilderService {

  build(
    lean: LeanFormSchema,
    registerTables: Record<string, Partial<RegisterTable>>,
    labelMap?: Record<string, string>,
  ): { schema: ConfigFormSchema; diagnostics: BuilderDiagnostic[] } {
    const diagnostics: BuilderDiagnostic[] = [];
    const effectiveLabelMap = labelMap ?? lean.labelMap;

    const schema: ConfigFormSchema = {
      version: lean.version,
      id: lean.id,
      name: lean.name,
      presets: lean.presets,
      sections: [],
    };

    // Build modeSelector
    if (lean.modeSelector) {
      const ms = lean.modeSelector;
      const table = registerTables[ms.registerTable];
      const register = table?.[ms.registerKey] as InverterRegister | undefined;

      let options: SelectOption[] = ms.options ?? [];
      if (options.length === 0 && register?.ui?.options) {
        options = register.ui.options.map(o => ({ value: o.value, label: o.label }));
      }

      options = this.resolveOptionLabels(options, effectiveLabelMap);

      const label = this.resolveLabel(
        ms.labelKey ?? ms.label,
        register?.ui?.labelKey,
        ms.registerKey,
        effectiveLabelMap,
      );

      schema.modeSelector = {
        key: ms.key ?? ms.registerKey,
        label,
        registerKey: ms.registerKey,
        options,
        registerTable: ms.registerTable,
        settingsCode: ms.settingsCode,
        alwaysIncludeInSectionWrites: ms.alwaysIncludeInSectionWrites,
        applyMode: ms.applyMode,
      };
    }

    // Build sections
    for (const section of lean.sections) {
      const table = registerTables[section.registerTable];
      if (!table) {
        diagnostics.push({
          level: 'error',
          message: `Register table "${section.registerTable}" not found`,
          sectionId: section.id,
        });
        continue;
      }

      const modeSection = this.buildSection(section, table, diagnostics, registerTables, effectiveLabelMap);
      schema.sections.push(modeSection);
    }

    return { schema, diagnostics };
  }

  private buildSection(
    section: LeanModeSection,
    table: Partial<RegisterTable>,
    diagnostics: BuilderDiagnostic[],
    registerTables: Record<string, Partial<RegisterTable>>,
    labelMap?: Record<string, string>,
  ): ModeSection {
    const fields: FieldDefinition[] = [];

    for (const ref of section.fields) {
      const register = table[ref.registerKey] as InverterRegister | undefined;
      if (!register) {
        diagnostics.push({
          level: 'error',
          message: `Register key "${ref.registerKey}" not found in table`,
          registerKey: ref.registerKey,
          sectionId: section.id,
        });
        continue;
      }

      if (!register.ui && !ref.fieldType) {
        diagnostics.push({
          level: 'warning',
          message: `Register "${ref.registerKey}" has no ui metadata, using defaults`,
          registerKey: ref.registerKey,
          sectionId: section.id,
        });
      }

      fields.push(this.buildField(ref, register, labelMap));
    }

    const label = this.resolveLabel(
      section.labelKey ?? section.label,
      undefined,
      section.id,
      labelMap,
    );

    // Build sectionSelector if present
    let sectionSelector: BuiltSectionSelector | undefined;
    if (section.sectionSelector) {
      const ss = section.sectionSelector;
      const ssTableName = ss.registerTable ?? section.registerTable;
      const ssTable = registerTables[ssTableName];
      const ssRegister = ssTable?.[ss.registerKey] as InverterRegister | undefined;

      let ssOptions: SelectOption[] = ss.options ?? [];
      if (ssOptions.length === 0 && ssRegister?.ui?.options) {
        ssOptions = ssRegister.ui.options.map(o => ({ value: o.value, label: o.label }));
      }

      ssOptions = this.resolveOptionLabels(ssOptions, labelMap);

      const ssLabel = this.resolveLabel(
        ss.labelKey,
        ssRegister?.ui?.labelKey,
        ss.registerKey,
        labelMap,
      );

      sectionSelector = {
        key: ss.key ?? ss.registerKey,
        label: ssLabel,
        registerKey: ss.registerKey,
        options: ssOptions,
        registerTable: ssTableName,
        applyMode: ss.applyMode,
        alwaysIncludeInSectionWrites: ss.alwaysIncludeInSectionWrites,
      };
    }

    return {
      id: section.id,
      label,
      modeValue: section.modeValue,
      fields,
      controlRegister: section.controlRegister,
      registerTable: section.registerTable,
      validityMask: section.validityMask,
      excludeModeSelectorOnSave: section.excludeModeSelectorOnSave,
      ...(sectionSelector ? { sectionSelector } : {}),
      ...(section.customContent ? { customContent: section.customContent } : {}),
      ...(section.writeBlockSize ? { writeBlockSize: section.writeBlockSize } : {}),
    };
  }

  private buildField(
    ref: LeanFieldRef,
    register: InverterRegister,
    labelMap?: Record<string, string>,
  ): FieldDefinition {
    const ui = register.ui;

    // Priority: ref > ui > register > default
    const fieldType: FieldType = ref.fieldType ?? ui?.fieldType ?? 'input';
    const key = ref.key ?? ref.registerKey;

    const label = this.resolveLabel(
      ref.labelKey,
      ui?.labelKey ?? register.custom?.translate,
      ref.registerKey,
      labelMap,
    );

    // Options: ref.constraint.options > ui.options > []
    let options: SelectOption[] = [];
    if (ref.constraint?.options) {
      options = ref.constraint.options;
    } else if (ui?.options) {
      options = ui.options.map(o => ({ value: o.value, label: o.label }));
    }

    options = this.resolveOptionLabels(options, labelMap);

    // Unit: ref > ui > register.limits > register
    const unit = ref.unit ?? ui?.displayUnit ?? register.limits?.unit ?? register.unit ?? undefined;

    // Transform: ref > ui > auto-computed from accuracy > none
    let transform = ref.transform ?? ui?.transform ?? undefined;
    let transformFactor = ref.transformFactor ?? ui?.transformFactor ?? undefined;

    // Auto-compute writeMultiply from register.accuracy when no explicit transform is set.
    // This reverses the accuracy factor applied during Modbus decode (getInverterAttribute).
    const isBitField = ref.fieldType === 'bit-toggle' || ref.fieldType === 'bit-select';
    if (!transform && !isBitField && register.accuracy != null && register.accuracy !== 1) {
      transform = 'writeMultiply';
      transformFactor = 1 / register.accuracy;
    }

    // Constraint (min/max/step): ref > ui > register.limits
    const constraint = this.resolveConstraint(ref, ui, register, options, labelMap);

    // InputType: ref > ui > default
    const inputType = ref.inputType ?? ui?.inputType ?? undefined;

    // SettingsCode: ref > ui
    const settingsCode = ref.settingsCode ?? ui?.settingsCode ?? undefined;

    // TriggerValue: ref > ui
    const triggerValue = ref.triggerValue ?? ui?.triggerValue ?? undefined;

    const field: FieldDefinition = {
      key,
      label,
      type: fieldType,
      registerKey: ref.registerKey,
    };

    if (inputType) field.inputType = inputType;
    if (constraint) field.constraint = constraint;
    if (unit) field.unit = unit;
    if (transform) field.transform = transform;
    if (transformFactor !== undefined) field.transformFactor = transformFactor;
    if (settingsCode) field.settingsCode = settingsCode;
    if (triggerValue !== undefined) field.triggerValue = triggerValue;
    if (ref.visibleWhen) field.visibleWhen = ref.visibleWhen;
    if (ref.crossFieldTransform) field.crossFieldTransform = ref.crossFieldTransform;
    if (ref.dynamicOptions) field.dynamicOptions = ref.dynamicOptions;
    if (ref.customTriggerHandler) field.customTriggerHandler = ref.customTriggerHandler;
    if (ref.bitIndex !== undefined) field.bitIndex = ref.bitIndex;
    if (ref.bitRange) field.bitRange = ref.bitRange;
    if (ref.bitRegisterKey) field.bitRegisterKey = ref.bitRegisterKey;

    return field;
  }

  private resolveLabel(
    primary: string | undefined,
    secondary: string | undefined,
    fallback: string,
    labelMap?: Record<string, string>,
  ): string {
    const raw = primary ?? secondary ?? fallback;
    if (labelMap?.[raw]) return labelMap[raw];
    if (labelMap && raw && isDevMode()) {
      console.warn(`[ConfigFormBuilder] label key "${raw}" not found in labelMap — showing raw ID`);
    }
    return raw;
  }

  private resolveOptionLabels(
    options: SelectOption[],
    labelMap?: Record<string, string>,
  ): SelectOption[] {
    if (!labelMap || options.length === 0) return options;
    return options.map(o => ({
      value: o.value,
      label: labelMap[o.label] ?? o.label,
    }));
  }

  private resolveConstraint(
    ref: LeanFieldRef,
    ui: RegisterUiMetadata | undefined,
    register: InverterRegister,
    options: SelectOption[],
    labelMap?: Record<string, string>,
  ): FieldDefinition['constraint'] | undefined {
    // Start with ref constraint if present
    if (ref.constraint) {
      const c = { ...ref.constraint };
      // Resolve constraint label via labelMap
      if (c.label && labelMap?.[c.label]) {
        c.label = labelMap[c.label];
      }
      // Ensure options are on the constraint for selects
      if (options.length > 0 && !c.options) {
        c.options = options;
      }
      return c;
    }

    // Build from ui + register
    const min = ui?.constraint?.min ?? register.limits?.min;
    const max = ui?.constraint?.max ?? register.limits?.max;
    const step = ui?.constraint?.step;

    if (options.length > 0 || min !== undefined || max !== undefined || step !== undefined) {
      const c: FieldDefinition['constraint'] = {};
      if (min !== undefined) c.min = min;
      if (max !== undefined) c.max = max;
      if (step !== undefined) c.step = step;
      if (options.length > 0) c.options = options;
      return c;
    }

    return undefined;
  }
}
